import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import NavigationHeader from '../../components/ui/NavigationHeader';
import api from '../../utils/api';
import { BookOpen, ChevronRight, FileText, ArrowLeft, ArrowRight, Clock, Eye, LayoutList, List, Share2, Layers, Calendar, CheckCircle2, Sparkles, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import CodePlayground from './CodePlayground';

/**
 * ArticleContent — renders article HTML and injects interactive CodePlayground
 * widgets after every <pre><code>…</code></pre> block it finds.
 */
const ArticleContent = ({ html }) => {
    const containerRef = useRef(null);
    const [playgrounds, setPlaygrounds] = useState([]);

    useEffect(() => {
        if (!containerRef.current) return;

        // Find all <pre> blocks that contain a <code> child
        const preBlocks = containerRef.current.querySelectorAll('pre');
        const items = [];

        preBlocks.forEach((pre, index) => {
            const codeEl = pre.querySelector('code');
            const rawText = codeEl ? codeEl.textContent : pre.textContent;
            if (!rawText || rawText.trim().length < 10) return; // skip trivial blocks

            // Hide the original pre block so it doesn't show up alongside the playground
            pre.style.display = 'none';

            // Create a mount point div after the <pre> if one doesn't already exist
            const mountId = `playground-mount-${index}`;
            let mount = pre.parentNode.querySelector(`#${mountId}`);
            if (!mount) {
                mount = document.createElement('div');
                mount.id = mountId;
                pre.parentNode.insertBefore(mount, pre.nextSibling);
            }

            items.push({ code: rawText.trim(), mountEl: mount });
        });

        setPlaygrounds(items);
    }, [html]);

    return (
        <>
            <div
                ref={containerRef}
                className="font-sans"
                dangerouslySetInnerHTML={{ __html: html }}
            />
            {/* Portal playgrounds into their mount points */}
            {playgrounds.map((pg, i) =>
                pg.mountEl
                    ? createPortal(
                          <CodePlayground key={i} initialCode={pg.code} />,
                          pg.mountEl
                      )
                    : null
            )}
        </>
    );
};



const ArticleReader = () => {
    const { categorySlug, articleSlug } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [article, setArticle] = useState(null);
    const [category, setCategory] = useState(null);
    const [parentCategory, setParentCategory] = useState(null);
    const [prevArticle, setPrevArticle] = useState(null);
    const [nextArticle, setNextArticle] = useState(null);
    const [siblingArticles, setSiblingArticles] = useState([]);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [isCompleted, setIsCompleted] = useState(false);
    const [markingComplete, setMarkingComplete] = useState(false);
    const [selection, setSelection] = useState(null);
    const articleRef = useRef(null);

    useEffect(() => {
        if (articleSlug) {
            fetchArticle();
        }
    }, [articleSlug]);

    // Emit context to AI Coach whenever article loads
    useEffect(() => {
        if (article) {
            const event = new CustomEvent('prep-coach-context-set', {
                detail: {
                    title: article.title,
                    content: article.content.replace(/<[^>]*>?/gm, "").substring(0, 3000),
                    open: false // Don't auto-open on page load
                }
            });
            window.dispatchEvent(event);
        }
    }, [article]);

    const handleAskAI = () => {
        if (article) {
            const event = new CustomEvent('prep-coach-context-set', {
                detail: {
                    title: article.title,
                    content: article.content.replace(/<[^>]*>?/gm, "").substring(0, 3000),
                    open: true // Explicit ask: do open
                }
            });
            window.dispatchEvent(event);
        }
    };

    useEffect(() => {
        const handleSelection = () => {
            const sel = window.getSelection();
            if (sel && sel.toString().trim().length > 5 && articleRef.current?.contains(sel.anchorNode)) {
                const range = sel.getRangeAt(0);
                const rect = range.getBoundingClientRect();
                setSelection({
                    text: sel.toString().trim(),
                    top: rect.top + window.scrollY - 45,
                    left: rect.left + rect.width / 2,
                });
            } else {
                setSelection(null);
            }
        };

        document.addEventListener('mouseup', handleSelection);
        return () => document.removeEventListener('mouseup', handleSelection);
    }, []);

    const handleAskSelection = () => {
        if (selection) {
            const event = new CustomEvent('prep-coach-ask-selection', {
                detail: { 
                    text: selection.text,
                    open: true // Explicit selection ask: do open
                }
            });
            window.dispatchEvent(event);
            setSelection(null);
        }
    };

    const fetchArticle = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/api/learn/articles/${articleSlug}`);
            const data = response.data;

            setArticle(data.article);
            setCategory(data.category);
            setParentCategory(data.parent_category);
            setPrevArticle(data.prev_article);
            setNextArticle(data.next_article);
            setSiblingArticles(data.sibling_articles || []);

            // Scroll to top on load
            window.scrollTo(0, 0);
        } catch (error) {
            console.error('Failed to fetch article:', error);
            toast.error('Article not found.');
            if (categorySlug) {
                navigate(`/learn/${categorySlug}`);
            } else {
                navigate('/learn');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleShare = () => {
        navigator.clipboard.writeText(window.location.href);
        toast.success("Link copied to clipboard!");
    };

    const handleMarkCompleted = async () => {
        if (markingComplete || isCompleted) return;
        try {
            setMarkingComplete(true);
            await api.post(`/api/learn/articles/${articleSlug}/complete`);
            setIsCompleted(true);
            toast.success('Article marked as completed! 🎉');
        } catch (error) {
            console.error('Failed to mark article as completed:', error);
            toast.error('Failed to mark as completed.');
        } finally {
            setMarkingComplete(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex flex-col">
                <NavigationHeader />
                <div className="flex-1 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-3">
                        <div className="w-8 h-8 rounded-full border-4 border-slate-200 border-t-emerald-600 animate-spin" />
                        <span className="text-slate-500 font-medium">Loading article...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!article) return null;

    if (!article) return null;

    return (
        <div className="min-h-screen bg-white flex flex-col font-sans relative overflow-hidden">
            <NavigationHeader />

            {/* Premium Article Header (Emerald Effect) */}
            <div className="relative pt-14 pb-0 overflow-hidden bg-gradient-to-b from-emerald-50 to-white font-sans">
                <div className="max-w-7xl mx-auto px-6 relative z-10">
                    {/* Compact Breadcrumbs & Actions Row */}
                    <div className="flex items-center justify-between mb-0 py-1">
                        <nav className="flex items-center text-[13px] font-medium text-slate-500 space-x-2 overflow-x-auto whitespace-nowrap hide-scrollbar pr-4">
                            <Link to="/learn" className="hover:text-emerald-600 transition-colors flex items-center gap-1.5 shrink-0">
                                <BookOpen size={14} /> Learn
                            </Link>
                            {parentCategory && (
                                <>
                                    <ChevronRight size={14} className="text-slate-300 stroke-[3] shrink-0" />
                                    <Link to={`/learn/${parentCategory.slug}`} className="hover:text-slate-900 transition-colors shrink-0">
                                        {parentCategory.name}
                                    </Link>
                                </>
                            )}
                            {category && (
                                <>
                                    <ChevronRight size={14} className="text-slate-300 stroke-[3] shrink-0" />
                                    <Link to={`/learn/${category.slug}`} className="hover:text-slate-900 transition-colors shrink-0">
                                        {category.name}
                                    </Link>
                                </>
                            )}
                            <ChevronRight size={14} className="text-slate-300 stroke-[3] shrink-0" />
                            <span className="text-slate-900 font-bold shrink-0">{article.title}</span>
                        </nav>

                        <div className="flex items-center gap-2 shrink-0">
                            <button onClick={handleShare} className="p-2.5 bg-white/60 text-slate-400 hover:text-emerald-600 hover:bg-white rounded-xl transition-all border border-slate-200/50 hover:border-emerald-200 shadow-sm" title="Share Article">
                                <Share2 size={16} />
                            </button>
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className={`lg:hidden p-2.5 rounded-xl transition-all border shadow-sm ${sidebarOpen ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}
                                title="Toggle Contents"
                            >
                                <List size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 w-full px-0 lg:pl-0 lg:pr-10 lg:pl-10 flex items-start relative lg:gap-12 xl:gap-24 mt-2">

                {/* Left Sidebar: Table of Contents / Siblings */}
                {sidebarOpen && (
                    <aside className={`
                        w-[280px] shrink-0 bg-white h-[calc(100vh-6rem)] overflow-y-auto custom-scrollbar
                        fixed inset-y-0 left-0 z-20 transform transition-transform duration-300 ease-in-out border-r border-slate-100
                        lg:sticky lg:top-[48px] lg:translate-x-0 pt-0 px-5 pb-12
                        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                    `}>
                        <div className="mb-6 flex items-center justify-between">
                            <h3 className="text-[10px] font-bold tracking-[0.15em] text-slate-400 uppercase">
                                IN THIS TOPIC
                            </h3>
                            <div className="w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold bg-slate-100 text-slate-500">
                                {siblingArticles.length > 0 ? siblingArticles.length : '3'}
                            </div>
                        </div>

                        <div className="mb-4">
                            <div className="font-bold text-emerald-500 flex items-center gap-2 mb-1 text-[15px]">
                                <Layers size={16} className="text-emerald-400" />
                                {category?.name || 'graphs'}
                            </div>
                        </div>

                        <nav className="space-y-1">
                            {/* Fallback to mock siblings if none exist to show the design */}
                            {(siblingArticles.length > 0 ? siblingArticles : [
                                { id: 1, title: 'Introduction to Graph Data Structure', slug: 'intro' },
                                { id: 2, title: 'Graph Representation Methods', slug: 'methods' },
                                { id: 3, title: 'Graph Traversal Algorithms (BFS & DFS)', slug: 'traversal' }
                            ]).map((sibling, index) => {
                                const isActive = sibling.id === article?.id || (siblingArticles.length === 0 && index === 0);
                                return (
                                    <Link
                                        key={sibling.id}
                                        to={`/learn/${category?.slug || 'graphs'}/${sibling.slug}`}
                                        className={`
                                            flex items-start gap-3 px-3 py-3 rounded-xl text-[13px] transition-all
                                            ${isActive
                                                ? 'bg-emerald-50 text-emerald-900 font-bold'
                                                : 'text-slate-600 hover:bg-slate-50 font-medium'}
                                        `}
                                    >
                                        <div className={`mt-[2px] flex items-center justify-center shrink-0 w-5 h-5 rounded text-[10px] font-bold ${isActive ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-400 group-hover:bg-slate-200'}`}>
                                            {index + 1}
                                        </div>
                                        <span className="leading-[1.4] mt-[1px]">
                                            {sibling.title}
                                        </span>
                                    </Link>
                                );
                            })}
                        </nav>

                        {parentCategory && (
                            <div className="mt-8 pt-6 border-t border-slate-100">
                                <Link to={`/learn/${parentCategory.slug}`} className="text-[13px] font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-2">
                                    <ArrowLeft size={16} /> Back to {parentCategory.name}
                                </Link>
                            </div>
                        )}
                    </aside>
                )}

                {/* Main Content Area */}
                <main className={`flex-1 min-w-0 bg-white px-8 sm:px-12 lg:pl-8 lg:pr-16 pt-0 pb-24 lg:mb-6 ${!sidebarOpen ? 'border-none' : ''}`}>

                    <article ref={articleRef} className="max-w-4xl relative">
                        <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight mb-4 leading-[1.15]">
                            {article.title}
                        </h1>

                        <div className="flex flex-wrap items-center gap-4 text-[13px] font-bold text-slate-400 mb-10tracking-widest">
                            <div className="flex items-center gap-1.5">
                                <Clock size={15} className="text-emerald-500" />
                                <span>{article.read_time_minutes || 7} min read</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Eye size={15} className="text-emerald-500" />
                                <span>{(article.views_count || 14).toLocaleString()} reads</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-emerald-600/80">
                                <Calendar size={15} />
                                <span>Updated {new Date(article.updated_at || '2026-03-07').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                            </div>
                        </div>

                        {/* Prose Content with Interactive Code Playgrounds */}
                        <ArticleContent html={article.content} />

                        {/* Sticky AI Invite at Bottom */}
                        <div className="my-12 p-8 rounded-3xl bg-emerald-50/50 border border-emerald-100 flex flex-col sm:flex-row items-center gap-6">
                            <div className="w-16 h-16 rounded-2xl bg-white border border-emerald-100 shadow-sm flex items-center justify-center shrink-0">
                                <Sparkles size={32} className="text-emerald-500 fill-emerald-50" />
                            </div>
                            <div className="flex-1 text-center sm:text-left">
                                <h3 className="text-lg font-bold text-slate-900 mb-1">Still have questions about {article.title}?</h3>
                                <p className="text-sm text-slate-600 mb-0">Our AI Coach has analyzed this article and is ready to help you master it. Just click the button to start a chat.</p>
                            </div>
                            <button 
                                onClick={handleAskAI}
                                className="px-6 py-3 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-colors shadow-lg active:scale-95 whitespace-nowrap"
                            >
                                Ask AI Tutor
                            </button>
                        </div>

                        {/* Navigation Footer — Prev | Mark Complete | Next */}
                        <div className="mt-16 pt-8 border-t border-slate-200">
                            <div className="flex items-center justify-between gap-3">
                                {/* Previous */}
                                <Link
                                    to={prevArticle ? `/learn/${category?.slug}/${prevArticle.slug}` : '#'}
                                    className={`flex items-center gap-2 px-6 py-3 rounded-lg border border-slate-200 text-sm font-bold transition-colors ${
                                        prevArticle
                                            ? 'text-slate-700 bg-white hover:bg-slate-50 hover:border-slate-300'
                                            : 'text-slate-400 bg-slate-50 cursor-not-allowed opacity-70'
                                    }`}
                                    onClick={(e) => !prevArticle && e.preventDefault()}
                                >
                                    <ArrowLeft size={16} /> Previous
                                </Link>

                                {/* Mark as Completed — center, close to Next */}
                                <div className="flex items-center gap-2 ml-auto">
                                    <button
                                        onClick={handleMarkCompleted}
                                        disabled={isCompleted || markingComplete}
                                        className={`group relative overflow-hidden flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-bold transition-all duration-200 ${
                                            isCompleted
                                                ? 'bg-emerald-50 text-emerald-600 border border-emerald-200 cursor-default'
                                                : markingComplete
                                                    ? 'bg-slate-100 text-slate-400 border border-slate-200 cursor-wait'
                                                    : 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-md shadow-emerald-500/20 active:scale-[0.98]'
                                        }`}
                                    >
                                        {markingComplete ? (
                                            <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-500 rounded-full animate-spin" />
                                        ) : (
                                            <CheckCircle2 size={16} className={isCompleted ? 'text-emerald-500' : ''} />
                                        )}
                                        {markingComplete ? 'Saving...' : isCompleted ? 'Completed' : 'Mark Done'}
                                    </button>

                                    {/* Next */}
                                    <Link
                                        to={nextArticle ? `/learn/${category?.slug}/${nextArticle.slug}` : '#'}
                                        className={`flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-bold transition-colors ${
                                            nextArticle
                                                ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                                                : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                        }`}
                                    >
                                        Next Topic <ArrowRight size={16} />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </article>

                </main>
            </div>

            {/* Mobile Sidebar Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-slate-900/20 z-10 lg:hidden backdrop-blur-sm"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Selection Tooltip */}
            {selection && (
                <div 
                    className="fixed z-[100] transform -translate-x-1/2 pointer-events-auto"
                    style={{ top: selection.top, left: selection.left }}
                >
                    <button
                        onClick={handleAskSelection}
                        className="flex items-center gap-2 px-3 py-1.5 bg-slate-900 text-white rounded-full text-[11px] font-bold shadow-2xl hover:bg-emerald-600 transition-all scale-100 hover:scale-105 active:scale-95 animate-in fade-in zoom-in duration-200"
                    >
                        <Sparkles size={12} className="fill-emerald-400 text-emerald-400" />
                        Ask AI Coach
                    </button>
                    <div className="w-2 h-2 bg-slate-900 rotate-45 mx-auto -mt-1 shadow-xl" />
                </div>
            )}
        </div>
    );
};

export default ArticleReader;
